package fyi.moko.hotpl.domain.photo

import fyi.moko.hotpl.domain.model.PhotoGroup
import fyi.moko.hotpl.domain.model.PhotoKind

/**
 * 분류 결과. [kind]가 null이면 종류까지는 못 가렸지만 [group]은 안다는 뜻이다.
 * [group]까지 null이면 아무것도 못 정했다.
 */
data class PhotoKindGuess(val kind: PhotoKind?, val group: PhotoGroup?) {
    companion object {
        val UNKNOWN = PhotoKindGuess(null, null)
        fun of(kind: PhotoKind) = PhotoKindGuess(kind, kind.group)
        fun groupOnly(group: PhotoGroup) = PhotoKindGuess(null, group)
    }
}

/**
 * 온디바이스 신호를 사진 종류로 옮긴다.
 *
 * ── 왜 6종을 다 맞히려 하지 않는가 ──────────────────────────────
 * 화면이 실제로 갈라야 하는 건 **가게 갤러리냐 그날 방문 기록이냐** 하나다. 실측(사진 38장,
 * VersionSandbox `docs/photo_classify_probe.md`)에서 이 축은 37장 중 36장이 맞았지만,
 * 같은 신호로 `외관`과 `내부`를 가르는 건 14장 중 9장에 그쳤다 — 유리 너머 내부가 보이는 근접
 * 외관 사진과 하늘이 안 나오는 야간 사진에서 무너진다. 그래서 **가릴 수 있는 것만 종류로
 * 내놓고 나머지는 묶음만** 돌려준다. 못 가린 걸 아무 값으로나 채우면 화면에는 정해지지 않은
 * 분류가 확정된 것처럼 뜬다.
 *
 * 확실히 가려지는 두 가지:
 *  · **메뉴판** — 글자 밀도. 메뉴판 15~76블록 대 나머지 0~7블록으로 구간이 비어 있다.
 *    형태(액자·스탠드·벽 부착·디지털 화면·칠판 손글씨)를 가리지 않았다.
 *  · **음식·음료** — Food/Cuisine/Meal 계열 라벨이 충분히 강할 때.
 *
 * 알려진 약점: 사람이 주인공인 사진을 못 잡는다. 바리스타나 단체 식사 사진은 화면에 음료가
 * 있다는 이유로 음식으로 간다. 라벨러에게 "누가 주인공인가"라는 개념이 없어서, 규칙을 손봐서
 * 메울 수 있는 종류의 문제가 아니다 — 사용자가 화면에서 고치는 쪽으로 넘긴다.
 */
object PhotoSignalMapper {

    /**
     * 글자 블록이 이만큼 넘으면 메뉴판으로 본다.
     *
     * 사진 38장에서 메뉴판은 15블록부터, 나머지는 7블록까지였다. 8~15가 비어 있어 그 중앙을
     * 골랐다. **이 값은 전체 데이터를 보고 정한 것**이라 정확도 수치를 낼 때 쓴 값(train 절반에서
     * 유도한 18)과 다르다 — 운영에 넣을 상수라 관측된 경계의 한가운데를 쓰는 게 맞다.
     */
    const val MENU_TEXT_BLOCKS = 12

    /** 이만큼 넘으면 음식 사진으로 본다. 신뢰도를 합산한 값이라 라벨 두세 개가 겹쳐야 넘는다. */
    private const val FOOD_SCORE = 1.0f

    /** 공간·구조물·인쇄물 — 가게에 붙는 사진의 신호. */
    private val PLACE_WEIGHTS = mapOf(
        "Menu" to 2.0f, "Poster" to 1.0f, "Paper" to 1.0f, "Screenshot" to 1.0f,
        "Building" to 1.0f, "Room" to 1.0f, "Roof" to 1.0f, "Skyscraper" to 1.0f, "Stadium" to 0.8f,
        "Chair" to 0.9f, "Desk" to 0.9f, "Couch" to 0.9f, "Bench" to 0.8f, "Shelf" to 0.8f,
        "Cabinetry" to 0.8f, "Countertop" to 0.8f, "Curtain" to 0.6f, "Tile" to 0.6f, "Armrest" to 0.6f,
        "Sky" to 0.7f, "Road" to 0.7f, "Asphalt" to 0.7f, "Space" to 0.5f, "Kitchen" to 0.5f, "Bar" to 0.5f,
    )

    /** 먹은 것과 사람 — 그 방문에 붙는 사진의 신호. */
    private val VISIT_WEIGHTS = mapOf(
        "Food" to 1.2f, "Cuisine" to 1.2f, "Meal" to 1.2f, "Lunch" to 1.0f, "Supper" to 1.0f,
        "Fast food" to 0.8f, "Eating" to 1.0f, "Gelato" to 1.0f, "Cake" to 0.8f, "Bread" to 0.8f,
        "Cookie" to 0.8f, "Pizza" to 0.8f, "Juice" to 0.7f, "Coffee" to 0.7f, "Wine" to 0.6f,
        "Cup" to 0.7f, "Saucer" to 0.6f, "Tableware" to 0.5f, "Porcelain" to 0.5f,
        "Selfie" to 1.2f, "Smile" to 0.8f, "Hand" to 0.8f, "Nail" to 0.6f, "Crowd" to 0.6f,
        "Interaction" to 0.8f, "Party" to 0.8f, "Team" to 0.6f,
    )

    /**
     * 밖에서 찍었다는 신호. 실내에서는 원칙적으로 프레임에 들어올 수 없는 것들이다.
     * `Roof`는 없다 — 실내 천장에도 붙는다.
     */
    private val OUTDOOR_WEIGHTS = mapOf(
        "Sky" to 1.0f, "Road" to 1.0f, "Asphalt" to 1.0f,
        "Vehicle" to 0.8f, "Car" to 0.8f, "Bicycle" to 0.8f,
        "Wheel" to 0.6f, "Tire" to 0.6f, "Bumper" to 0.6f, "Windshield" to 0.6f,
        "Skyscraper" to 0.8f, "Stadium" to 0.5f,
    )

    /**
     * 이만큼 넘으면 가게 사진 중에서도 **외관**이라고 특정한다.
     *
     * 하나의 선으로 외관과 내부를 가르는 건 여전히 불가능하다 — 야외 점수에서 외관 최저 0.268,
     * 내부 최고 0.331로 겹친다. 그래서 **외관 한 방향만** 확정하고 내부는 계속 침묵한다.
     * "점수가 0이면 내부"도 검토했지만 버렸다: 33장으로 넓히면 음식·사람·메뉴판 15장이 전부
     * 0이라 접사 사진이 모두 내부로 확정되고, [PhotoKind.INTERIOR]는 대표 사진 후보라
     * 음식 사진이 가게 카드에 올라간다.
     *
     * 값은 임계값을 훑어 **답이 변하지 않는 구간**(0.40~0.85)의 한가운데를 골랐다. 0.35가 외관을
     * 한 장 더 잡지만 한 칸 아래(0.30)에서 오답 2장이 나오는 절벽 위라 쓰지 않는다.
     * 실측 33장에서 외관이라 말한 6장이 전부 정답이고, 실제 외관 9장 중 3장은 침묵한다.
     * VersionSandbox `docs/photo_classify_probe.md` 13차, 채점기는 `probe/map_facade.py`.
     */
    const val OUTDOOR_SCORE = 0.62

    /** 이 라벨들이 충분히 강하면 방문 사진 중에서도 음식이라고 특정한다. */
    private val FOOD_LABELS = setOf(
        "Food", "Cuisine", "Meal", "Lunch", "Supper", "Gelato", "Cake", "Bread",
        "Cookie", "Pizza", "Fast food",
    )

    fun map(signals: PhotoSignals): PhotoKindGuess {
        // 글자 밀도가 먼저다. 메뉴판에는 음식 사진이 실려 있어서 라벨만 보면 음식으로 샌다 —
        // 라벨만 썼을 때 메뉴판 10장 중 3장이 그렇게 방문 쪽으로 갔다.
        if (signals.textBlocks >= MENU_TEXT_BLOCKS) return PhotoKindGuess.of(PhotoKind.MENU)

        if (signals.labels.isEmpty()) return PhotoKindGuess.UNKNOWN

        val place = signals.labels.sumOf { (PLACE_WEIGHTS[it.label] ?: 0f) * it.confidence.toDouble() }
        val visit = signals.labels.sumOf { (VISIT_WEIGHTS[it.label] ?: 0f) * it.confidence.toDouble() }
        if (place == 0.0 && visit == 0.0) return PhotoKindGuess.UNKNOWN

        if (place >= visit) {
            // 야외 신호가 충분히 강하면 외관으로 특정한다 — 여기서만 [PhotoKind.STOREFRONT]가
            // 나온다. 이게 없으면 간판 사진의 kind가 null로 남아 [PhotoKind.COVER_PRIORITY]가
            // 간판을 맨 앞에 두고도 실제 대표는 음식·메뉴판이 된다.
            val outdoor = signals.labels
                .sumOf { (OUTDOOR_WEIGHTS[it.label] ?: 0f) * it.confidence.toDouble() }
            if (outdoor >= OUTDOOR_SCORE) return PhotoKindGuess.of(PhotoKind.STOREFRONT)

            // 내부인지 아닌지는 여전히 못 가린다. 지어내지 않고 묶음만 돌려준다.
            return PhotoKindGuess.groupOnly(PhotoGroup.PLACE)
        }
        val food = signals.labels
            .filter { it.label in FOOD_LABELS }
            .sumOf { it.confidence.toDouble() }
        return if (food >= FOOD_SCORE) {
            PhotoKindGuess.of(PhotoKind.FOOD_DRINK)
        } else {
            PhotoKindGuess.groupOnly(PhotoGroup.VISIT)
        }
    }
}
